<script setup lang="ts">
import {IonButton} from '@ionic/vue'
import {computed, onBeforeMount, Ref, ref} from 'vue'
import {getLabelForShift} from '@/helper/shiftTypes'
import {fetchGlobalShiftHullTimes} from "@/helper/globalShiftTimes"
import {
    convertMinutesToShiftTime,
    convertMinutesToTime,
    convertShiftTimeToMinutes,
    shiftTimesExceedHullTimes
} from '@/helper/datetime/timeRangeHelper'
import {AvailabilityShiftDefault, ShiftTimes, ShiftType} from "@/graphql/generated/graphql";
import TextWithIcon from '@/components/TextWithIcon.vue'
import Modal from '@/components/Modal.vue'
import {ShiftTime} from "@/helper/calendar/shiftTime";
import InlineSVG from '@/components/InlineSVG.vue';
import Range from '@/components/Range.vue'

const props = defineProps<{
    availabilityShiftDefaults: AvailabilityShiftDefault[];
}>();

const emit = defineEmits(['update:availableShiftDefault']);

const editItem = ref<ShiftType|undefined>(undefined)
const editorModalOpen = ref<boolean>(false)

const availabilityShiftIconMap = {
    FD: '/icons/sun-horizon.svg',
    ZD: '/icons/sun-dim.svg',
    SD: '/icons/sun.svg',
    ND: '/icons/moon-stars.svg',
}

const openEditShiftDefaultsModal  = (shift:AvailabilityShiftDefault) => {
    editItem.value = shift.type
    editorModalOpen.value = true
}

const shifts = ref([])
const hullTimes : Ref<ShiftTimes[]> = ref([])

type Ding = {[key in ShiftType]: {begin: number, until: number}}

const shiftBoundariesInMinutes = computed(() => {
    const boundaryThresholdInMinutes = 120

    return hullTimes.value.reduce((acc, hullTime) => {
        acc[hullTime.type] = convertShiftTimeToMinutes(hullTime)
        acc[hullTime.type].begin-= boundaryThresholdInMinutes
        acc[hullTime.type].until+= boundaryThresholdInMinutes
        return acc
    }, {} as Ding)
})

const shiftsInMinutes = computed(() => {
    return props.availabilityShiftDefaults.reduce((acc, shift) => {
        acc[shift.type] = convertShiftTimeToMinutes(shift)
        return acc
    }, {} as Ding)
})

const getHullInformationStringForShift = (shift: ShiftType) => {
    const times = hullTimes.value.find(hullTime => hullTime.type === shift)
    const shiftLabel = getLabelForShift(shift)

    return times
        ? `Die meisten ${shiftLabel}e finden zwischen ${times.begin} Uhr und ${times.until} Uhr statt.`
        : undefined
}

const isInHull = (shiftType: ShiftType) => {
    const hullTimeShift:ShiftTimes|undefined = hullTimes.value.find(hullTime => hullTime.type === shiftType)
    if(!hullTimeShift){
        return true
    }
    const hullTime = new ShiftTime(
        hullTimeShift.begin,
        hullTimeShift.until
    )

    const shift:AvailabilityShiftDefault|undefined = props.availabilityShiftDefaults.find(llShift => llShift.type==shiftType)
    if(!shift){
        return true
    }
    const shiftTime = new ShiftTime(
        shift.begin,
        shift.until
    )

    return(!shiftTimesExceedHullTimes(shiftTime, hullTime))
}

const pinFormatter = (value:number) => {
    return convertMinutesToTime(value) + ' Uhr'
}

const fetchHullTimes = () => {
    return fetchGlobalShiftHullTimes()
        .then(res => hullTimes.value = res)
}

const onChangeShift = (event: any, shift: ShiftType) => {
    const values = event.detail.value
    const times = convertMinutesToShiftTime(values.lower, values.upper)

    const newAvailabilityShiftDefault: AvailabilityShiftDefault = {
        begin: times.begin,
        until: times.until,
        type: shift
    }
    emit('update:availableShiftDefault', newAvailabilityShiftDefault);
}

onBeforeMount(() => {
    fetchHullTimes();
})
</script>

<template>
    <div class="availability-shifts">
        <div v-for="shift in availabilityShiftDefaults">
            <div @click="()=> {openEditShiftDefaultsModal(shift) }">
                <InlineSVG :src="availabilityShiftIconMap[shift.type]" />
                <div class="type"> {{ shift.type }}</div>
                <div class="times">{{ shift.begin }} - {{ shift.until }}</div>
            </div>
        </div>
    </div>
    <div class="text-hint margin-top-regular transparent">
        Die Änderung der Schichtzeiten wirkt sich nur auf Verfügbarkeiten aus, die du nach der Änderung erstellst. Vorher erstellte Verfügbarkeiten behalten ihre bisherigen Schichtzeiten.
    </div>

    <Modal
        v-if="editItem"
        v-model="editorModalOpen"
        :title="`Mein ${getLabelForShift(editItem)}`"
        >
        <div class="shift-time-editor" v-if="hullTimes.length">
            <Range
                aria-label="Custom range"
                :dual-knobs="true"
                :min="shiftBoundariesInMinutes[editItem]?.begin"
                :max="shiftBoundariesInMinutes[editItem]?.until"
                :value="{lower: shiftsInMinutes[editItem].begin, upper: shiftsInMinutes[editItem].until}"
                :pin="true"
                :ticks="false"
                :snaps="true"
                :step="15"
                :pin-formatter="pinFormatter"
                v-on:ion-change="($event) => {editItem ? onChangeShift($event, editItem) : null}"
            />
            <TextWithIcon
                v-if="getHullInformationStringForShift(editItem)"
                :small="true"
                :warning="!isInHull(editItem)"
                :success="isInHull(editItem)"
                class="margin-top-regular no-margin-bottom"
                >
                    <template #icons>
                        <InlineSVG
                            v-if="!isInHull(editItem)"
                            src="/icons/info.svg"
                        />
                        <InlineSVG
                            v-else
                            src="/icons/check-circle.svg"
                        />
                    </template>
                    <p>{{ getHullInformationStringForShift(editItem) }}</p>
            </TextWithIcon>
        </div>
        <template v-slot:actions>
            <IonButton expand="block" color="primary" @click="editorModalOpen=false">Ok</IonButton>
        </template>

</Modal>


</template>

<style lang="scss" scoped>
.shift-time-editor {
    .hull-information {
        background: var(--ion-color-light);
        color: var(--ion-color-medium);
        padding: 1rem;
        border-radius: 1rem;
        margin-top: 0.5rem;
    }
}
.availability-shifts {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    > div {
        flex: 0 0 31%;

        > div {
            border-radius: 1rem;
            background-color: var(--ion-color-grey-tint-3);

            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;


            > .inline-svg {
                width: 2.5rem;
                margin-top: 0.75rem;
                margin-bottom: 0.25rem;
                color: var(--ion-color-primary);
                // should be a mixin
                filter: drop-shadow(0 0 0.4rem rgba(var(--ion-color-primary-rgb), 0.5));
            }

            > .type {
                font-weight: var(--custom-font-weight-extrabold);
                color: var(--ion-color-dark);
            }

            > .times {
                font-size: var(--custom-font-size-regular);
                color: var(--ion-color-dark);
                margin-top: 0.5em;
                margin-bottom: 1rem;
                text-align: center;
            }
        }
    }
}
</style>
